// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectOneOrganization,
  type Organization,
} from '@gandi/redux-organization';
import {
  CreateForm,
  actions,
  SubmitButton,
  type CreateFormProps,
  type TemplateFormData,
} from '@gandi/template2';
import { useTranslator } from '@gandi/translate';
import { Button, Base } from '@gandi/ui';

import { selectSetupOwner } from 'src/Cart/Setup/modules/selectors';
import type { CreateOrTransfer } from 'src/Cart/Setup/types';
import { useAppSelector } from 'src/hooks/useAppSelector';

import Editor from './Editor';

export type Props = {
  onSubmitSuccess: $NonMaybeType<CreateFormProps['onSubmitSuccess']>,
  onSubmitFail: CreateFormProps['onSubmitFail'],
  process: CreateOrTransfer,
  backUrl: string,
};

const Create = ({
  onSubmitSuccess,
  onSubmitFail,
  process,
  backUrl,
}: Props): React.Node => {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const sharingId = useAppSelector(selectSetupOwner);
  const orga: ?Organization = useAppSelector(state =>
    selectOneOrganization(state, sharingId),
  );

  const transformInitialValues = (formData: TemplateFormData) => ({
    ...formData,
    sharingId,
    // become unused, could be dropped some day (cf. @gandi/template2)
    orgname: orga?.name || orga?.orgname || '',
  });

  const handleSubmit: Props['onSubmitSuccess'] = ({ template, formValues }) => {
    if (
      (process === 'transfer' && formValues.default_transfer) ||
      (process === 'create' && formValues.default_registration)
    ) {
      // $FlowFixMe[incompatible-call]
      dispatch(
        actions.setDefaultTemplate({
          productType: 'domain',
          process,
          templateId: template.id,
          sharingId,
        }),
      );
    }

    return onSubmitSuccess({ template, formValues });
  };

  return (
    <CreateForm
      onSubmitSuccess={handleSubmit}
      onSubmitFail={onSubmitFail}
      transformInitialValues={transformInitialValues}
    >
      <Base marginBottom="base">
        <Editor isNew process={process} />
      </Base>
      <Base marginBottom="base">
        <Button grouped kind="outline" to={backUrl}>
          {__('Cancel')}
        </Button>
        <SubmitButton grouped type="submit">
          {__('Create and apply')}
        </SubmitButton>
      </Base>
    </CreateForm>
  );
};

export default Create;
